import React from "react";
import { Info, Palette, Layers, Settings2, Plus, Trash2, ShieldAlert, Sparkles, X } from "lucide-react";
import type { 
  MapStyleRenderStackEditorProps, 
  MapStylePresentationColorBindingDescriptor,
  MapStylePresentationLayoutControlDescriptor,
} from "../../contracts/map-workspace";
import type { 
  LayerExperienceCategoricalVariant,
  LayerExperienceRangeVariant,
} from "../../types/layer-experience";
import { Label } from "../../internal/primitives/label";
import { Input } from "../../internal/primitives/input";
import { Separator } from "../../internal/primitives/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../internal/primitives/select";
import { Switch } from "../../internal/primitives/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../internal/primitives/tabs";
import { MapIconPicker } from "./MapIconPicker";

export function MapStyleRenderStackEditor({
  appearance,
  availableFields,
  editorDescriptor,
  iconPicker,
  glyphBundle,
  rulesModeBody,
  onBack,
  onUpdate,
}: MapStyleRenderStackEditorProps) {
  if (!editorDescriptor) return null;

  // Resolve active variant from entry_id
  const activeVariant = appearance.variants.find(v => v.id === editorDescriptor.entry_id);
  
  if (!activeVariant) return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center space-y-3">
      <ShieldAlert className="w-8 h-8 text-muted-foreground/40" />
      <div className="space-y-1">
        <p className="text-sm font-bold">Entry Not Found</p>
        <p className="text-xs text-muted-foreground">The requested style entry could not be resolved.</p>
      </div>
      <button onClick={onBack} className="text-xs font-bold text-primary hover:underline">Go Back</button>
    </div>
  );

  const isLabelOverlay = editorDescriptor.stack_role === "overlay_label";
  const symbolContentMode =
    editorDescriptor.symbol_content_mode ??
    (isLabelOverlay ? "glyph" : "sprite");

  const handleRepresentationChange = (rep: string) => {
    const nextVariants = appearance.variants.map(v => {
      if (v.id !== activeVariant.id) return v;
      const currentStyle = v.style || {};
      const currentLayout = ((currentStyle as any).layout || {}) as Record<string, unknown>;
      const currentPaint = ((currentStyle as any).paint || {}) as Record<string, unknown>;
      const nextLayout = { ...currentLayout };
      const nextPaint = { ...currentPaint };
      if (rep !== "symbol") {
        delete nextLayout["icon-image"];
        delete nextLayout["text-field"];
        delete nextPaint["icon-color"];
        delete nextPaint["icon-halo-color"];
        delete nextPaint["text-halo-color"];
      }
      return {
        ...v,
        style: {
          ...currentStyle,
          representation: rep as any,
          layout: nextLayout,
          paint: nextPaint,
        }
      };
    });
    onUpdate({ variants: nextVariants });
  };

  const handleFieldChange = (field: string) => {
    const nextVariants = appearance.variants.map(v => {
      if (v.id !== activeVariant.id) return v;
      if (v.kind === "categorical" || v.kind === "range") {
        return { ...v, field };
      }
      return v;
    });
    onUpdate({ variants: nextVariants });
  };

  const updateStyleProp = (target: "paint" | "layout", prop: string, value: any) => {
    const nextVariants = appearance.variants.map(v => {
      if (v.id !== activeVariant.id) return v;
      const style = v.style || {};
      const currentSection = { ...(((style as any)[target] || {}) as Record<string, unknown>) };

      if (target === "layout") {
        if (prop === "text-offset" && value !== undefined) {
          delete currentSection["text-radial-offset"];
        }
        if (prop === "text-radial-offset" && value !== undefined) {
          delete currentSection["text-offset"];
        }
      }

      if (value === undefined) {
        delete currentSection[prop];
      } else {
        currentSection[prop] = value;
      }
      
      return {
        ...v,
        style: {
          ...style,
          [target]: currentSection
        }
      };
    });
    onUpdate({ variants: nextVariants });
  };

  const handleAddCategory = () => {
    if (activeVariant.kind !== "categorical") return;
    const nextVariants = appearance.variants.map(v => {
      if (v.id !== activeVariant.id || v.kind !== "categorical") return v;
      return {
        ...v,
        categories: [
          ...v.categories,
          { value: "new_value", label: "New Category", paint: { [getPaintProperty("primary", v.style?.representation)]: "primary" } }
        ]
      };
    });
    onUpdate({ variants: nextVariants });
  };

  const handleRemoveCategory = (index: number) => {
    if (activeVariant.kind !== "categorical") return;
    const nextVariants = appearance.variants.map(v => {
      if (v.id !== activeVariant.id || v.kind !== "categorical") return v;
      return {
        ...v,
        categories: v.categories.filter((_, i) => i !== index)
      };
    });
    onUpdate({ variants: nextVariants });
  };

  const handleUpdateCategory = (index: number, patch: any) => {
    if (activeVariant.kind !== "categorical") return;
    const nextVariants = appearance.variants.map(v => {
      if (v.id !== activeVariant.id || v.kind !== "categorical") return v;
      const categories = [...v.categories];
      categories[index] = { ...categories[index], ...patch };
      return { ...v, categories };
    });
    onUpdate({ variants: nextVariants });
  };

  const handleAddBreak = () => {
    if (activeVariant.kind !== "range") return;
    const nextVariants = appearance.variants.map(v => {
      if (v.id !== activeVariant.id || v.kind !== "range") return v;
      const lastMax = v.breaks[v.breaks.length - 1]?.max || 0;
      return {
        ...v,
        breaks: [
          ...v.breaks,
          { label: "New Break", min: lastMax, max: lastMax + 10, paint: { [getPaintProperty("primary", v.style?.representation)]: "primary" } }
        ]
      };
    });
    onUpdate({ variants: nextVariants });
  };

  const handleRemoveBreak = (index: number) => {
    if (activeVariant.kind !== "range") return;
    const nextVariants = appearance.variants.map(v => {
      if (v.id !== activeVariant.id || v.kind !== "range") return v;
      return {
        ...v,
        breaks: v.breaks.filter((_, i) => i !== index)
      };
    });
    onUpdate({ variants: nextVariants });
  };

  const handleUpdateBreak = (index: number, patch: any) => {
    if (activeVariant.kind !== "range") return;
    const nextVariants = appearance.variants.map(v => {
      if (v.id !== activeVariant.id || v.kind !== "range") return v;
      const breaks = [...v.breaks];
      breaks[index] = { ...breaks[index], ...patch };
      return { ...v, breaks };
    });
    onUpdate({ variants: nextVariants });
  };

  const showRepresentation = !isLabelOverlay;
  const showDataField = activeVariant.kind !== "single" && activeVariant.kind !== "rules";
  const showBaseConfig = showRepresentation || showDataField;
  const topLevelRuleLayoutProps = new Set([
    "icon-allow-overlap",
    "icon-ignore-placement",
    "text-allow-overlap",
    "text-ignore-placement",
  ]);
  const visibleLayoutControls = activeVariant.kind === "rules"
    ? editorDescriptor.layout_controls.filter(control => topLevelRuleLayoutProps.has(control.prop))
    : editorDescriptor.layout_controls;

  return (
    <div className="flex flex-col h-full bg-transparent">
      <div className="space-y-8 pb-12">
        {/* Base Configuration */}
        {showBaseConfig && (
          <div className="grid gap-5">
            <EditorSectionHeader 
              title="Base Configuration" 
              icon={<Settings2 className="w-3.5 h-3.5" />} 
            />
            
            <div className="space-y-4 px-0.5">
              {showRepresentation && (
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase font-bold text-muted-foreground/50 tracking-wide">Representation</Label>
                  <Select 
                    value={activeVariant.style?.representation || "fill"} 
                    onValueChange={handleRepresentationChange}
                  >
                    <SelectTrigger className="h-8 text-[11px] bg-muted/10 border-muted-foreground/5 hover:bg-muted/20 transition-colors">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {editorDescriptor.representation_options.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} disabled={opt.state === "not_implemented"}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {showDataField && (
                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1">
                  <Label className="text-[10px] uppercase font-bold text-muted-foreground/50 tracking-wide">Data Field</Label>
                  <Select 
                    value={(activeVariant as any).field} 
                    onValueChange={handleFieldChange}
                  >
                    <SelectTrigger className="h-8 text-[11px] bg-muted/10 border-muted-foreground/5 hover:bg-muted/20 transition-colors">
                      <SelectValue placeholder="Select a field..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableFields.map(f => (
                        <SelectItem key={f.name} value={f.name}>
                          <div className="flex items-center justify-between gap-4 w-full min-w-[140px]">
                            <span className="font-medium">{f.name}</span>
                            <span className="text-[9px] uppercase font-black opacity-30">{f.type}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <Separator className="bg-border/20" />
          </div>
        )}

        {/* Symbol Controls */}
        {activeVariant.kind !== "rules" && activeVariant.style?.representation === "symbol" && (
          <>
            <div className="space-y-5">
              <EditorSectionHeader 
                title="Symbol Styles" 
                icon={<Sparkles className="w-3.5 h-3.5" />} 
              />
              
              <div className="space-y-5 px-0.5">
                {glyphBundle && symbolContentMode === "glyph" && !iconPicker && (
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground/50 tracking-wide">Glyph Bundle</Label>
                    <Select 
                      value={glyphBundle.selectedBundleId || ""} 
                      onValueChange={glyphBundle.onSelectBundle}
                    >
                      <SelectTrigger className="h-8 text-[11px] bg-muted/10 border-muted-foreground/5 hover:bg-muted/20 transition-colors">
                        <SelectValue placeholder="Select bundle..." />
                      </SelectTrigger>
                      <SelectContent>
                        {glyphBundle.bundleOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {!isLabelOverlay && symbolContentMode === "sprite" && iconPicker && (
                  <div className="space-y-3">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground/50 tracking-wide">Sprite Icon</Label>
                    <MapIconPicker
                      presentation="inline"
                      value={(activeVariant.style?.layout as any)?.["icon-image"] || null}
                      searchValue={iconPicker.searchValue || ""}
                      activeSetFilter={iconPicker.activeSetFilter || "all"}
                      setFilterOptions={iconPicker.setFilterOptions}
                      iconSets={iconPicker.iconSets}
                      loading={iconPicker.loading}
                      label="Sprite Icon"
                      onSearchChange={iconPicker.onSearchChange || (() => {})}
                      onSetFilterChange={iconPicker.onSetFilterChange || (() => {})}
                      onClear={() => updateStyleProp("layout", "icon-image", null)}
                      onSelect={(id) => {
                        const icon = iconPicker.iconSets.flatMap(s => s.icons).find(i => i.id === id);
                        if (icon) {
                          updateStyleProp("layout", "icon-image", icon.token);
                          iconPicker.onSelectIcon?.({
                            iconId: icon.id,
                            token: icon.token,
                            setId: icon.setId,
                            setLabel: icon.setLabel
                          });
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <Separator className="bg-border/20" />
          </>
        )}

        {/* Label Text Controls */}
        {activeVariant.kind !== "rules" && editorDescriptor.text_binding_controls && editorDescriptor.text_binding_controls.length > 0 && (
          <>
            <div className="space-y-5">
              <EditorSectionHeader 
                title="Label Text" 
                icon={<Settings2 className="w-3.5 h-3.5" />} 
              />
              
              <div className="grid gap-4 px-0.5">
                {editorDescriptor.text_binding_controls.map(control => (
                  <div key={control.id} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Label className="text-[10px] uppercase font-bold text-muted-foreground/50 tracking-wide">{control.label}</Label>
                        <div title={control.description}>
                          <Info className="w-3 h-3 text-muted-foreground/30 cursor-help" />
                        </div>
                      </div>
                    </div>
                    <Input
                      value={(activeVariant.style?.layout as any)?.[control.prop] ?? control.current_value ?? ""}
                      onChange={(e) => updateStyleProp("layout", control.prop, e.target.value)}
                      placeholder={control.placeholder}
                      className="h-8 text-[11px] bg-muted/10 border-muted-foreground/5 focus:bg-background transition-colors"
                    />
                  </div>
                ))}
              </div>
            </div>
            <Separator className="bg-border/20" />
          </>
        )}

        {/* Layout & Placement Controls */}
        {visibleLayoutControls.length > 0 && (
          <>
            <div className="space-y-5">
              <EditorSectionHeader 
                title="Layout & Placement" 
                icon={<Settings2 className="w-3.5 h-3.5" />} 
              />
              
              <div className="grid gap-4 px-0.5">
                {visibleLayoutControls.map(control => (
                  <LayoutControl
                    key={control.id}
                    control={control}
                    value={(activeVariant.style as any)?.[control.target]?.[control.prop]}
                    onChange={(val) => updateStyleProp(control.target, control.prop, val)}
                  />
                ))}
              </div>
            </div>
            <Separator className="bg-border/20" />
          </>
        )}

        {/* Color Bindings */}
        {activeVariant.kind !== "rules" && (
          <div className="space-y-6">
            <EditorSectionHeader 
              title="Color Definitions" 
              icon={<Palette className="w-3.5 h-3.5" />} 
            />
            
            <div className="space-y-8 px-0.5">
              {editorDescriptor.color_bindings.map(binding => {
                const paintProp = getPaintProperty(binding.id, activeVariant.style?.representation);
                return (
                  <ColorBindingControl
                    key={binding.id}
                    binding={binding}
                    value={activeVariant.style?.paint?.[paintProp]}
                    onChange={(val) => updateStyleProp("paint", paintProp, val)}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Rule Logic Mode Body */}
        {activeVariant.kind === "rules" && rulesModeBody && (
          <div className="space-y-4 animate-in fade-in zoom-in-95 duration-200">
            <EditorSectionHeader 
              title="Rule Logic" 
              icon={<Layers className="w-3.5 h-3.5" />} 
            />
            <div className="p-0.5">
              {rulesModeBody}
            </div>
          </div>
        )}

        {/* Categories / Breaks */}
        {(activeVariant.kind === "categorical" || activeVariant.kind === "range") && (
          <div className="space-y-5 animate-in fade-in slide-in-from-bottom-2">
            <div className="flex items-center justify-between">
              <EditorSectionHeader 
                title={activeVariant.kind === "categorical" ? "Categories" : "Value Breaks"} 
                icon={<Plus className="w-3.5 h-3.5" />} 
              />
              <button 
                onClick={activeVariant.kind === "categorical" ? handleAddCategory : handleAddBreak}
                className="text-[10px] font-bold uppercase tracking-wider text-primary hover:bg-primary/5 px-2 py-1 rounded transition-colors"
              >
                {activeVariant.kind === "categorical" ? "Add Category" : "Add Break"}
              </button>
            </div>
            
            <div className="space-y-6 px-0.5">
              {activeVariant.kind === "categorical" && (activeVariant as LayerExperienceCategoricalVariant).categories?.map((cat, idx) => {
                const colorProp = getPaintProperty("primary", activeVariant.style?.representation);
                const colorValue = cat.paint?.[colorProp];
                const primaryBinding = editorDescriptor.color_bindings.find(b => b.id === "primary");

                return (
                  <div key={idx} className="space-y-4 pb-6 border-b border-border/10 last:border-0 group relative">
                    <button 
                      onClick={() => handleRemoveCategory(idx)}
                      className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-md transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-[9px] uppercase font-bold text-muted-foreground/40 tracking-wider">Value</Label>
                        <Input 
                          value={cat.value} 
                          onChange={(e) => handleUpdateCategory(idx, { value: e.target.value })}
                          className="h-8 text-[11px] bg-muted/5 border-muted-foreground/5 focus:bg-background transition-colors"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[9px] uppercase font-bold text-muted-foreground/40 tracking-wider">Label</Label>
                        <Input 
                          value={cat.label || ""} 
                          onChange={(e) => handleUpdateCategory(idx, { label: e.target.value })}
                          className="h-8 text-[11px] bg-muted/5 border-muted-foreground/5 focus:bg-background transition-colors"
                        />
                      </div>
                    </div>
                    {primaryBinding && (
                      <ColorBindingControl
                        binding={{
                          ...primaryBinding,
                          label: "Category Color",
                          description: "Color for this specific category"
                        }}
                        value={colorValue}
                        onChange={(val) => handleUpdateCategory(idx, { paint: { ...cat.paint, [colorProp]: val } })}
                      />
                    )}
                  </div>
                );
              })}
              
              {activeVariant.kind === "range" && (activeVariant as LayerExperienceRangeVariant).breaks?.map((brk, idx) => {
                const colorProp = getPaintProperty("primary", activeVariant.style?.representation);
                const colorValue = brk.paint?.[colorProp];
                const primaryBinding = editorDescriptor.color_bindings.find(b => b.id === "primary");

                return (
                  <div key={idx} className="space-y-4 pb-6 border-b border-border/10 last:border-0 group relative">
                    <button 
                      onClick={() => handleRemoveBreak(idx)}
                      className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-md transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-[9px] uppercase font-bold text-muted-foreground/40 tracking-wider">Min</Label>
                        <Input 
                          type="number"
                          value={brk.min ?? ""} 
                          onChange={(e) => handleUpdateBreak(idx, { min: e.target.value === "" ? null : Number(e.target.value) })}
                          className="h-8 text-[11px] bg-muted/5 border-muted-foreground/5 focus:bg-background transition-colors"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[9px] uppercase font-bold text-muted-foreground/40 tracking-wider">Max</Label>
                        <Input 
                          type="number"
                          value={brk.max ?? ""} 
                          onChange={(e) => handleUpdateBreak(idx, { max: e.target.value === "" ? null : Number(e.target.value) })}
                          className="h-8 text-[11px] bg-muted/5 border-muted-foreground/5 focus:bg-background transition-colors"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[9px] uppercase font-bold text-muted-foreground/40 tracking-wider">Label</Label>
                        <Input 
                          value={brk.label || ""} 
                          onChange={(e) => handleUpdateBreak(idx, { label: e.target.value })}
                          className="h-8 text-[11px] bg-muted/5 border-muted-foreground/5 focus:bg-background transition-colors"
                        />
                      </div>
                    </div>
                    {primaryBinding && (
                      <ColorBindingControl
                        binding={{
                          ...primaryBinding,
                          label: "Break Color",
                          description: "Color for this specific value range"
                        }}
                        value={colorValue}
                        onChange={(val) => handleUpdateBreak(idx, { paint: { ...brk.paint, [colorProp]: val } })}
                      />
                    )}
                  </div>
                );
              })}

              {((activeVariant.kind === "categorical" && (activeVariant as LayerExperienceCategoricalVariant).categories?.length === 0) || 
                (activeVariant.kind === "range" && (activeVariant as LayerExperienceRangeVariant).breaks?.length === 0)) && (
                <div className="py-10 border border-dashed rounded-lg flex flex-col items-center justify-center text-center space-y-3 bg-muted/5 border-muted-foreground/10">
                  <Palette className="w-6 h-6 text-muted-foreground/20" />
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">No entries defined</p>
                  <button 
                    onClick={activeVariant.kind === "categorical" ? handleAddCategory : handleAddBreak}
                    className="text-[10px] font-black text-primary hover:underline"
                  >
                    Create First Entry
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LayoutControl({ 
  control, 
  value, 
  onChange 
}: { 
  control: MapStylePresentationLayoutControlDescriptor, 
  value?: any, 
  onChange: (val: any) => void 
}) {
  const effectiveValue = value !== undefined ? value : control.current_value;

  // Local state for number-list inputs to handle partial entry (e.g. while typing "0.")
  const [localX, setLocalX] = React.useState<string>("");
  const [localY, setLocalY] = React.useState<string>("");

  // Sync local state with effectiveValue when it changes externally
  React.useEffect(() => {
    if (control.input === "number-list") {
      const valX = Array.isArray(effectiveValue) && effectiveValue.length === 2 ? String(effectiveValue[0]) : "";
      const valY = Array.isArray(effectiveValue) && effectiveValue.length === 2 ? String(effectiveValue[1]) : "";
      
      const numX = parseFloat(valX);
      const numY = parseFloat(valY);
      const curX = parseFloat(localX);
      const curY = parseFloat(localY);

      // Only update local state if the numeric value actually changed to preserve partial typing
      if (isNaN(numX)) {
        if (!isNaN(curX) || (valX === "" && localX !== "")) setLocalX(valX);
      } else if (numX !== curX) {
        setLocalX(valX);
      }

      if (isNaN(numY)) {
        if (!isNaN(curY) || (valY === "" && localY !== "")) setLocalY(valY);
      } else if (numY !== curY) {
        setLocalY(valY);
      }
    }
  }, [control.input, effectiveValue, localX, localY]);

  const handleCoordinateChange = (index: number, val: string) => {
    const nextX = index === 0 ? val : localX;
    const nextY = index === 1 ? val : localY;
    
    if (index === 0) setLocalX(val);
    else setLocalY(val);

    const numX = parseFloat(nextX);
    const numY = parseFloat(nextY);

    if (nextX === "" && nextY === "") {
      onChange(undefined);
    } else if (!isNaN(numX) && !isNaN(numY)) {
      onChange([numX, numY]);
    }
  };

  const handleNumberChange = (val: string) => {
    if (val === "") {
      onChange(undefined);
      return;
    }
    const num = parseFloat(val);
    if (!isNaN(num)) {
      onChange(num);
    }
  };

  const renderInput = () => {
    switch (control.input) {
      case "select":
        return (
          <Select 
            value={String(effectiveValue ?? "")} 
            onValueChange={onChange}
          >
            <SelectTrigger className="h-8 text-[11px] bg-muted/10 border-muted-foreground/5 hover:bg-muted/20 transition-colors">
              <SelectValue placeholder={control.placeholder || "Select..."} />
            </SelectTrigger>
            <SelectContent>
              {control.options?.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "number":
        return (
          <Input
            type="number"
            value={effectiveValue ?? ""}
            onChange={(e) => handleNumberChange(e.target.value)}
            placeholder={control.placeholder}
            min={control.min}
            max={control.max}
            step={control.step}
            className="h-8 text-[11px] bg-muted/10 border-muted-foreground/5 focus:bg-background transition-colors"
          />
        );
      case "number-list":
        return (
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[9px] uppercase font-bold text-muted-foreground/30">X</Label>
              <Input
                type="number"
                step={control.step ?? 0.1}
                value={localX}
                onChange={(e) => handleCoordinateChange(0, e.target.value)}
                placeholder="0"
                className="h-8 text-[11px] bg-muted/10 border-muted-foreground/5 focus:bg-background transition-colors"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[9px] uppercase font-bold text-muted-foreground/30">Y</Label>
              <Input
                type="number"
                step={control.step ?? 0.1}
                value={localY}
                onChange={(e) => handleCoordinateChange(1, e.target.value)}
                placeholder="0"
                className="h-8 text-[11px] bg-muted/10 border-muted-foreground/5 focus:bg-background transition-colors"
              />
            </div>
          </div>
        );
      case "boolean":
        return (
          <div className="flex min-h-8 items-center rounded-md border border-muted-foreground/5 bg-muted/10 px-3">
            <div className="flex flex-1 flex-col gap-0.5">
              <span className="text-[11px] font-medium text-foreground">
                {effectiveValue === true ? "Enabled" : "Disabled"}
              </span>
              <span className="text-[10px] text-muted-foreground/60">
                {control.description}
              </span>
            </div>
            <Switch checked={effectiveValue === true} onCheckedChange={onChange} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Label className="text-[10px] uppercase font-bold text-muted-foreground/50 tracking-wide">{control.label}</Label>
          <div title={control.description}>
            <Info className="w-3 h-3 text-muted-foreground/30 cursor-help" />
          </div>
        </div>
      </div>
      {renderInput()}
    </div>
  );
}

function EditorSectionHeader({ title, icon }: { title: string, icon: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="text-muted-foreground/40">
        {icon}
      </div>
      <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground/70">{title}</Label>
    </div>
  );
}

function ColorBindingControl({ 
  binding, 
  value, 
  onChange 
}: { 
  binding: MapStylePresentationColorBindingDescriptor & { current_value?: any }, 
  value?: any, 
  onChange: (val: any) => void 
}) {
  const localValueIsSemanticObject =
    Boolean(value) && typeof value === "object" && (value as { kind?: string }).kind === "semantic";
  const localValueIsHexLiteral = typeof value === "string" && value.startsWith("#");
  const localValueIsResolvedCssLiteral =
    typeof value === "string" &&
    (value.startsWith("rgb(") ||
      value.startsWith("rgba(") ||
      value.startsWith("hsl(") ||
      value.startsWith("hsla(") ||
      value.startsWith("oklch(") ||
      value.startsWith("oklab(") ||
      value.startsWith("lab(") ||
      value.startsWith("lch("));
  const localValueIsSemanticToken =
    typeof value === "string" &&
    value.length > 0 &&
    !localValueIsHexLiteral &&
    !localValueIsResolvedCssLiteral;
  const shouldUseLocalValue =
    value !== undefined &&
    (value === "" || localValueIsSemanticObject || localValueIsHexLiteral || localValueIsSemanticToken);
  const effectiveValue = shouldUseLocalValue
    ? value
    : binding.current_value !== undefined
      ? binding.current_value
      : value;
  const isLiteral = typeof effectiveValue === "string" && effectiveValue.startsWith("#");
  
  // Extract token string for the Select component to ensure placeholder visibility
  const tokenValue = React.useMemo(() => {
    // 1. If value is explicitly a hex literal, it's not a theme token
    if (typeof value === "string" && value.startsWith("#")) return undefined;
    
    // 2. If value is explicitly empty, it's cleared
    if (value === "") return undefined;

    // Helper to check if a value is semantic and return its token
    const getSemanticToken = (v: any) => {
      if (typeof v === "string" && binding.semantic_options.some(opt => opt.token === v)) return v;
      if (v && typeof v === "object" && v.kind === "semantic") return v.token;
      return undefined;
    };

    // 3. Try effective value first (handles current selection or initial binding value)
    const current = getSemanticToken(effectiveValue);
    if (current) return current;

    // 4. Check binding.current_value as fallback for resolved literals (rgb/hsl) in value prop
    return getSemanticToken(binding.current_value);
  }, [value, effectiveValue, binding.current_value, binding.semantic_options]);

  const [mode, setMode] = React.useState<"theme" | "custom">(isLiteral ? "custom" : "theme");

  // Sync mode and ensure internal consistency if value changes externally
  React.useEffect(() => {
    if (typeof effectiveValue === "string" && effectiveValue.startsWith("#")) {
      setMode("custom");
    } else if (effectiveValue) {
      setMode("theme");
    }
  }, [effectiveValue]);

  const handleTokenChange = (token: string) => {
    // If the existing value is a semantic object, preserve its structure while updating the token
    if (effectiveValue && typeof effectiveValue === "object" && effectiveValue.kind === "semantic") {
      onChange({ ...effectiveValue, token });
    } else {
      onChange(token);
    }
  };

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between px-0.5">
        <div className="flex items-center gap-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60">{binding.label}</Label>
          <div title={binding.description}>
            <Info className="w-3 h-3 text-muted-foreground/30 cursor-help" />
          </div>
        </div>
        {effectiveValue && (
          <button 
            onClick={() => onChange("")}
            className="text-[9px] font-bold uppercase text-muted-foreground/30 hover:text-destructive transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      <Tabs value={mode} onValueChange={(v: string) => setMode(v as any)} className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-7 p-0.5 bg-muted/20 border-none rounded-md mb-2">
          <TabsTrigger value="theme" className="text-[9px] font-bold uppercase py-0 h-full rounded-[4px] data-[state=active]:bg-background data-[state=active]:shadow-sm">Theme</TabsTrigger>
          <TabsTrigger value="custom" className="text-[9px] font-bold uppercase py-0 h-full rounded-[4px] data-[state=active]:bg-background data-[state=active]:shadow-sm" disabled={!binding.supports_literal_color}>Custom</TabsTrigger>
        </TabsList>
        
        <TabsContent value="theme" className="mt-0 focus-visible:ring-0">
          <Select 
            value={tokenValue} 
            onValueChange={handleTokenChange}
          >
            <SelectTrigger className="h-8 text-[11px] bg-muted/10 border-muted-foreground/5 hover:bg-muted/20 transition-colors">
              <SelectValue placeholder="Choose theme token..." />
            </SelectTrigger>
            <SelectContent>
              {binding.semantic_options.map(opt => (
                <SelectItem key={opt.token} value={opt.token}>
                   <div className="flex items-center gap-2">
                     <div 
                       className="w-3 h-3 rounded-full border border-black/10 shadow-sm" 
                       style={{ backgroundColor: opt.swatch_color || "#ccc" }} 
                     />
                     <span className="font-medium text-[11px]">{opt.label}</span>
                   </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TabsContent>

        <TabsContent value="custom" className="mt-0 focus-visible:ring-0">
          <div className="flex gap-2 items-center">
            <div className="relative w-8 h-8 rounded-md border border-muted-foreground/10 overflow-hidden shrink-0 shadow-sm bg-muted/5">
              <input 
                type="color" 
                value={isLiteral ? effectiveValue : "#000000"} 
                onChange={(e) => onChange(e.target.value)}
                className="absolute inset-0 w-[150%] h-[150%] -translate-x-[15%] -translate-y-[15%] cursor-pointer border-none p-0 bg-transparent"
              />
              {!isLiteral && <X className="absolute inset-0 m-auto w-3 h-3 text-muted-foreground/20" />}
            </div>
            <div className="relative flex-1">
              <Input 
                value={typeof effectiveValue === "string" ? effectiveValue : ""} 
                onChange={(e) => onChange(e.target.value)}
                placeholder="#HEX Value"
                className="h-8 text-[11px] font-mono pl-6 bg-muted/5 border-muted-foreground/5 focus:bg-background transition-colors"
              />
              <div className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-muted-foreground/30">#</div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function getPaintProperty(bindingId: string, representation?: string | null): string {
  const rep = representation || "fill";
  
  if (bindingId === "primary") {
    switch (rep) {
      case "fill": return "fill-color";
      case "line": return "line-color";
      case "circle": return "circle-color";
      case "symbol": return "text-color";
      case "fill-extrusion": return "fill-extrusion-color";
      default: return "color";
    }
  }

  if (bindingId === "stroke") {
    switch (rep) {
      case "fill": return "fill-outline-color";
      case "circle": return "circle-stroke-color";
      default: return "stroke-color";
    }
  }

  if (bindingId === "halo") {
    switch (rep) {
      case "symbol": return "text-halo-color";
      case "circle": return "circle-blur";
      default: return "halo-color";
    }
  }

  if (bindingId === "icon") {
    return "icon-color";
  }

  if (bindingId === "icon_halo") {
    return "icon-halo-color";
  }

  return bindingId;
}
